// The small icon vocabulary of the Fortress card face:
//
// - TokenTray: an Action's uses — N token slots; a circular arrow = they clear
//   at the end of every round (else they stay all game). Filled slots are the
//   tokens currently on the card.
// - IconLineView: a passive drawn as icons — resource gains (dashed one-time
//   icons = into the bank, solid permanent icons = production; a slash between
//   icons = pick one), extra actions (the action bolt on a terracotta token),
//   and range bonuses (the pieces it applies to, drawn like the build pattern;
//   attack = red sword, defend = blue shield, the board's aim colours).
// - OrRule: the "— or —" between a pick-one passive's options.

import type { ReactNode } from "react";
import { ResourceIcon } from "../resources";
import type { IconLine, RangeTarget, ResourceKey } from "./cardText";

const INK = "#1a1a22";
const CREAM = "#faf6ea";
/** PixiGame's AIM_ATTACK_COLOR / AIM_DEFEND_COLOR. */
const ATTACK = "#d7263d";
const DEFEND = "#2e86ab";

function Svg24({ size, label, children }: { size: number; label?: string; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: "block", flex: "none" }}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      {children}
    </svg>
  );
}

/** Circular arrow: tokens clear at the end of every round. */
export function ResetGlyph({ size = 16 }: { size?: number }) {
  return (
    <Svg24 size={size}>
      <path
        d="M19 12a7 7 0 1 1-2.05-4.95M19 4.5v3.2h-3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg24>
  );
}

/** Summit flag: the tray is a win counter. */
function FlagGlyph({ size = 16 }: { size?: number }) {
  return (
    <Svg24 size={size}>
      <path
        d="M6 21V4M6 4h11l-2.4 3.8L17 11.6H6"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg24>
  );
}

export interface TraySpec {
  readonly count: number;
  readonly perRound: boolean;
  /** A win counter (win at `count` tokens) rather than an Action's uses. */
  readonly win?: boolean;
}

/** An Action's token slots (or a win card's token counter). */
export function TokenTray({
  spec,
  filled = 0,
  dark = false,
}: {
  spec: TraySpec;
  filled?: number;
  dark?: boolean;
}) {
  const { count, perRound, win } = spec;
  const used = Math.max(0, Math.min(count, filled));
  const title = win
    ? `Win at ${count} token${count === 1 ? "" : "s"}${perRound ? ", cleared each round" : ""}`
    : `Up to ${count} use${count === 1 ? "" : "s"} per ${perRound ? "round" : "game"}${
        used ? ` — ${used} used` : ""
      }`;
  return (
    <span className={`fcard-tray${dark ? " is-dark" : ""}`}>
      <span className="fcard-tray-slots" role="img" aria-label={title} title={title}>
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={`fcard-slot${i < used ? " is-filled" : ""}`} />
        ))}
        {perRound && (
          <>
            <span className="fcard-tray-sep" />
            <span className="fcard-tray-icon">
              <ResetGlyph />
            </span>
          </>
        )}
        {win && (
          <>
            <span className="fcard-tray-sep" />
            <span className="fcard-tray-icon is-win">
              <FlagGlyph />
            </span>
          </>
        )}
      </span>
      <span className="fcard-tray-label" aria-hidden>
        {win ? `${count} TO WIN` : `${count} / ${perRound ? "ROUND" : "GAME"}`}
      </span>
    </span>
  );
}

/** An extra action: the action bolt on a terracotta token. */
export function ActionTokenIcon({ size = 34 }: { size?: number }) {
  return (
    <span className="fcard-icon" style={{ margin: -size * 0.15 }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        role="img"
        aria-label="extra action"
        style={{ display: "block" }}
      >
        <circle cx="12" cy="12" r="7.4" fill="#b4533a" stroke="#5e2416" strokeWidth="1.8" />
        <g transform="translate(4.56 4.56) scale(0.62)">
          <path d="M13 2.5 5 13.5h6l-1 8 8-11h-6z" fill={CREAM} />
        </g>
      </svg>
    </span>
  );
}

const RANGE_LABEL: Record<RangeTarget, string> = {
  scout: "scouts",
  barracks: "barracks",
  mine: "mines",
  bridge: "bridges",
  attack: "attack range",
  defend: "defend range",
};

/** A range target, drawn like the build pattern's pieces (ink / cream), or the
 * barracks' attack sword (red) / defend shield (blue). */
export function RangeGlyph({ kind, size = 24, dark = false }: { kind: RangeTarget; size?: number; dark?: boolean }) {
  const c = kind === "attack" ? ATTACK : kind === "defend" ? DEFEND : dark ? CREAM : INK;
  const body = (() => {
    switch (kind) {
      case "scout":
        return <circle cx="12" cy="12" r="7.2" fill={c} />;
      case "barracks":
        return <path d="M12 3.6 20.8 19H3.2z" fill={c} stroke={c} strokeWidth="1.2" strokeLinejoin="round" />;
      case "mine":
        return <rect x="5.2" y="5.2" width="13.6" height="13.6" rx="1.6" fill={c} />;
      case "bridge":
        return (
          <path
            d="M5.5 12h13M5.5 6.5v11M18.5 6.5v11"
            fill="none"
            stroke={c}
            strokeWidth="3.6"
            strokeLinecap="round"
          />
        );
      case "attack":
        return (
          <g fill={c}>
            <path d="M12 1.8 14.6 5.4V14.4H9.4V5.4z" />
            <rect x="5.6" y="13.6" width="12.8" height="3.4" rx="1.2" />
            <rect x="10.4" y="16.4" width="3.2" height="4" />
            <circle cx="12" cy="21.2" r="1.9" />
          </g>
        );
      case "defend":
        return <path d="M12 2.8 20 6.2v5.6c0 4.9-3.4 8.6-8 10.4-4.6-1.8-8-5.5-8-10.4V6.2z" fill={c} />;
    }
  })();
  return (
    <Svg24 size={size} label={RANGE_LABEL[kind]}>
      {body}
    </Svg24>
  );
}

/** "+N", with an optional unit suffix: range bonuses print "+NR" (R = range)
 * so a range chip reads differently from a "+N" resource/action gain. */
function Amount({ n, unit }: { n: number; unit?: string }) {
  return (
    <span className="fcard-amount">
      +{n}
      {unit && <span className="fcard-amount-unit">{unit}</span>}
    </span>
  );
}

const RESOURCE_NAME: Record<ResourceKey, string> = {
  minerals: "mineral",
  food: "food",
  vigor: "energy",
  obsidian: "obsidian",
};

/** One passive drawn as icons — each "+N …" bonus in its own chip. `text` is
 * the printed sentence, kept as the line's accessible name (and tooltip). */
export function IconLineView({ line, text, dark = false }: { line: IconLine; text: string; dark?: boolean }) {
  let body: ReactNode;
  switch (line.kind) {
    case "gain":
      body = (
        <span className="fcard-chip">
          <Amount n={line.amount} />
          {line.resources.map((r, i) => (
            <span key={r} className="fcard-gain-item">
              {i > 0 && <span className="fcard-slash" aria-hidden>/</span>}
              <span className="fcard-icon" title={RESOURCE_NAME[r]}>
                <ResourceIcon kind={r} size={44} oneTime={!line.production} />
              </span>
            </span>
          ))}
        </span>
      );
      break;
    case "actions":
      body = (
        <span className="fcard-chip">
          <Amount n={line.amount} />
          <ActionTokenIcon size={38} />
        </span>
      );
      break;
    case "range":
      // One chip per amount, so "+3 (pieces)" and "+1 (sword, shield)" read
      // as separate bonuses.
      body = line.groups.map((g, i) => (
        <span key={i} className="fcard-chip fcard-range-group">
          <Amount n={g.amount} unit="R" />
          {g.targets.map((t) => (
            <RangeGlyph key={t} kind={t} dark={dark} />
          ))}
        </span>
      ));
      break;
  }
  return (
    <p className={`fcard-iconline is-${line.kind}`} role="img" aria-label={text} title={text}>
      {body}
    </p>
  );
}

/** "— or —" between a pick-one passive's options. */
export function OrRule() {
  return (
    <span className="fcard-or">
      <span className="fcard-or-dash" aria-hidden />
      or
      <span className="fcard-or-dash" aria-hidden />
    </span>
  );
}
